import type { Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { supabase } from './supabase-client.ts';

export type SessionState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: Session };

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    // onAuthStateChange fires INITIAL_SESSION asynchronously during client
    // bootstrap. If it lands before getSession() resolves, we must not let
    // the older getSession() result overwrite it.
    let authEventSeen = false;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled || authEventSeen) return;
        setState(
          data.session ? { status: 'signed-in', session: data.session } : { status: 'signed-out' },
        );
      })
      .catch(() => {
        // Without this, a getSession() rejection would leave us stuck in
        // 'loading' forever. Treat it as signed-out so the LoginPage renders.
        if (cancelled || authEventSeen) return;
        setState({ status: 'signed-out' });
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      authEventSeen = true;
      setState(session ? { status: 'signed-in', session } : { status: 'signed-out' });
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}
